import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useAppContext } from '@prisjakt/ui-app-context';
import Breadcrumb, { BreadcrumbPath } from '@prisjakt/ui-breadcrumb';
import {
  FontWeight,
  H2Text,
  H3Text,
  BodyMediumText,
} from '@prisjakt/ui/text/variants';
import {
  KeywordSearchPageQuery,
  Offer as OfferType,
  Product,
} from '../../../types';
import { OffersGrid } from '../../product-collection/components/offers-grid';
import Offer from '../../product-collection/components/offers/offer';
import HeaderBlock from '../../product-collection/components/header-block';
import * as S from './styles';
import { NavigationSliceView } from '../../product-collection/components/navigation/navigation-component';
import { ProductLayout } from './product-layout';
import {
  trackKeywordSearchPageCategoryClick,
  trackKeywordSearchPageOfferClick,
  trackKeywordSearchPageProductClick,
  trackKeywordSearchPageNoImages,
  trackKeywordSearchPageOffersOnlyVisited,
} from '../tracking';
import WordCloud from 'src/components/word-cloud';
import { FacetLink } from '../../product-collection/components/navigation/types';

const UNCATEGORIZED_CATEGORY_ID = '2016';

const KeyWordLandingPageComponent = ({
  data,
}: {
  data: KeywordSearchPageQuery;
}) => {
  const dispatch = useDispatch();

  useEffect(() => {
    // Explicitly tell ads to update visibility/load (needed when lazy loading pages)
    dispatch({
      type: 'ADS_VIEW_UPDATE',
      view: 'keywordLandingPage:mounted',
    });
  }, [dispatch]);

  const { t } = useAppContext();
  const title = data.keywordLandingPage ? data.keywordLandingPage.title : '';
  const onProductClick = useCallback(
    ({ id }: Product) => trackKeywordSearchPageProductClick(title, id),
    [title]
  );
  const onOfferClick = useCallback(
    ({ id }: OfferType) => trackKeywordSearchPageOfferClick(title, id),
    [title]
  );
  const onCategoryClick = useCallback(
    (category: FacetLink) =>
      trackKeywordSearchPageCategoryClick(title, category.id),
    [title]
  );
  if (!data.keywordLandingPage?.products) {
    return null;
  }
  const {
    categories,
    products,
    offers,
    parentCategory,
    popularSearchTerms,
  } = data.keywordLandingPage;
  const links = data.keywordLandingPage?.categories
    .filter(({ id }) => id !== UNCATEGORIZED_CATEGORY_ID)
    .map(({ id, name, url, imageUrl }) => ({
      id,
      value: name,
      path: url,
      imageUrl,
      indexable: true,
      selected: false,
    }));
  // if true we know its a offers only kwlp
  if (products.length <= 0 && offers.length > 0) {
    trackKeywordSearchPageOffersOnlyVisited(
      data.keywordLandingPage!.title,
      data.keywordLandingPage!.url
    );
  }

  const noImagesOfferIDs = offers
    .map(offer => {
      if (!offer.media.first) return offer.id;
      return null;
    })
    .filter(Boolean);

  const noImagesProductsIDs = products
    .map(product => {
      if (!product.media.first) return product.id;
      return null;
    })
    .filter(Boolean);

  if (noImagesOfferIDs.length > 0 || noImagesProductsIDs.length > 0) {
    trackKeywordSearchPageNoImages(
      data.keywordLandingPage!.url,
      [...noImagesProductsIDs, ...noImagesOfferIDs].join(',')
    );
  }

  return (
    <S.WrapPageContainer withAds topPlacements={[]}>
      <Breadcrumb
        homeText={t('common', 'breadcrumb.home')}
        path={(parentCategory && Array.isArray(parentCategory.path)
          ? (parentCategory.path as BreadcrumbPath[])
          : []
        ).concat({
          id: title,
          name: title[0].toUpperCase() + title.slice(1),
          pathName: '',
        })}
      />
      <HeaderBlock title={title} />
      {!offers?.length && !products?.length ? (
        <>
          <S.WrapBody>
            <S.BodyGutter>
              <BodyMediumText>
                {t('category', 'keyword-landing-page.no-offers')}
              </BodyMediumText>
            </S.BodyGutter>
          </S.WrapBody>
          {links.length > 0 && (
            <S.WrapBody>
              <S.BodyGutter>
                <H3Text>
                  {t('category', 'keyword-landing-page.related-categories')}
                </H3Text>
              </S.BodyGutter>
              <NavigationSliceView
                links={links}
                onClick={onCategoryClick}
                onClickPreventDefault={false}
              />
            </S.WrapBody>
          )}
        </>
      ) : (
        products.length > 0 && (
          <NavigationSliceView
            links={links}
            onClick={onCategoryClick}
            onClickPreventDefault={false}
          />
        )
      )}
      {products?.length > 0 && (
        <ProductLayout
          products={(products as any) as Product[]}
          categories={categories}
          onClick={onProductClick}
        />
      )}
      {offers?.length > 0 && (
        <>
          <S.WrapOffers>
            <H2Text weight={FontWeight.HEAVIER}>
              {t('category', 'keyword-landing-page.offers-title')}
            </H2Text>
            <OffersGrid
              offers={offers
                .sort(
                  (o0, o1) =>
                    // Show offers with images first (false=-1, true=1)
                    Number(!!o1.media.first) - Number(!!o0.media.first)
                )
                .map(offer => (
                  <Offer
                    key={`${offer.id}_${offer.store.id}`}
                    offer={offer as OfferType}
                    onClick={onOfferClick}
                  />
                ))}
            />
          </S.WrapOffers>
        </>
      )}
      {Array.isArray(popularSearchTerms) && popularSearchTerms.length ? (
        <WordCloud
          title={t('search', 'search.related-searches')}
          items={popularSearchTerms}
          visibleItems={100}
        />
      ) : null}
    </S.WrapPageContainer>
  );
};

export default KeyWordLandingPageComponent;
